import React, { useState, useEffect } from 'react';
import { ExperimentType, ReactionType, ChemExperimentState, CalcResult, CalcDefinition, CalcInputRef, CalcScope, CalcCategory } from './types';
import { Calculator, CheckCircle, Plus, X } from 'lucide-react';
import { getCalculationsForExperiment, getCalculation } from './chemConfig';
import { calculationEngine, formatCalcResult, validateFormula } from './calculationEngine';

interface CalculationsTabProps {
  experiment: ChemExperimentState;
  experimentType: ExperimentType;
  reactionType: ReactionType;
  onResult?: (result: CalcResult) => void;
}

/**
 * CalculationsTab Component (Phase 2: Data-Driven)
 * 
 * Dynamically renders calculations based on:
 * 1. Current experiment type (API/FD/ADL)
 * 2. Reaction template configuration
 * 3. Calculation definitions from chemConfig
 * 
 * Features:
 * - Template-driven calculation list
 * - Automatic BOM value extraction
 * - Manual input support
 * - Result history
 * - Configurable formula evaluation
 */
export const CalculationsTab: React.FC<CalculationsTabProps> = ({
  experiment,
  experimentType,
  reactionType,
  onResult,
}) => {
  const [selectedCalcId, setSelectedCalcId] = useState<string | null>(null);
  const [manualInputs, setManualInputs] = useState<Record<string, number>>({});
  const [results, setResults] = useState<CalcResult[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [showCustomCalcForm, setShowCustomCalcForm] = useState(false);
  const [customCalculations, setCustomCalculations] = useState<CalcDefinition[]>([]);

  // Load custom calculations from localStorage
  useEffect(() => {
    const stored = localStorage.getItem(`custom-calculations-${experiment.experimentId}`);
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        setCustomCalculations(parsed);
      } catch (e) {
        console.error('Failed to load custom calculations:', e);
      }
    }
  }, [experiment.experimentId]);

  // Save custom calculations to localStorage
  const saveCustomCalculations = (calcs: CalcDefinition[]) => {
    setCustomCalculations(calcs);
    localStorage.setItem(`custom-calculations-${experiment.experimentId}`, JSON.stringify(calcs));
  };

  // Get all available calculations (predefined + custom)
  const predefinedCalculations = getCalculationsForExperiment(experimentType, reactionType);
  const customForCurrentScope = customCalculations.filter(
    (calc) => calc.scope.includes(experimentType)
  );
  const availableCalculations = [...predefinedCalculations, ...customForCurrentScope];

  // Get selected calculation (from predefined or custom)
  const selectedCalc = selectedCalcId
    ? getCalculation(selectedCalcId) || customCalculations.find((c) => c.id === selectedCalcId)
    : null;

  /**
   * Handle calculation selection
   */
  const handleSelectCalculation = (calcId: string) => {
    setSelectedCalcId(calcId);
    setManualInputs({});
    setError(null);
  };

  /**
   * Handle manual input change (Phase 4: supports variables, Phase 2: supports inputs)
   */
  const handleInputChange = (varOrInputId: string, value: string) => {
    const numValue = parseFloat(value);
    setManualInputs((prev) => ({
      ...prev,
      [varOrInputId]: isNaN(numValue) ? 0 : numValue,
    }));
  };

  /**
   * Get derived value from BOM (Phase 4: bomAggregate, Phase 2: bomTotal)
   */
  const getDerivedValue = (varOrInputId: string, source?: any): number => {
    if (!selectedCalc) return 0;

    // Phase 4: Check variables first
    if (selectedCalc.variables) {
      const variable = selectedCalc.variables.find((v) => v.name === varOrInputId);
      if (!variable || variable.source.type !== 'bomAggregate') return 0;

      const { role, field, mode } = variable.source;
      const relevant = experiment.materials.filter((m) => m.role === role);

      const values = relevant
        .map((m) => {
          if (field === 'quantity') return m.quantity;
          if (field === 'metadata.molWeight') {
            return m.metadata?.molWeight as number | undefined;
          }
          if (field === 'metadata.purity') {
            return m.metadata?.purity as number | undefined;
          }
          return undefined;
        })
        .filter((x): x is number => typeof x === 'number' && !isNaN(x));

      if (!values.length) return 0;

      if (mode === 'first') return values[0];
      if (mode === 'average') {
        return values.reduce((a, b) => a + b, 0) / values.length;
      }
      return values.reduce((a, b) => a + b, 0);
    }

    // Phase 2: Legacy bomTotal
    if (selectedCalc.inputs) {
      const input = selectedCalc.inputs.find((i) => i.id === varOrInputId);
      if (!input || input.source.type !== 'bomTotal') return 0;

      const role = input.source.role;
      return experiment.materials
        .filter((m) => m.role === role)
        .reduce((sum, m) => sum + (m.quantity || 0), 0);
    }

    return 0;
  };

  /**
   * Check if inputs are valid (Phase 4: check required variables, Phase 2: check required inputs)
   */
  const areInputsValid = (): boolean => {
    if (!selectedCalc) return false;

    // Phase 4: Check required variables
    if (selectedCalc.variables) {
      for (const variable of selectedCalc.variables) {
        if (variable.required && variable.source.type === 'manual') {
          const value = manualInputs[variable.name];
          if (value == null || isNaN(value)) {
            return false;
          }
        }
      }
      return true;
    }

    // Phase 2: Check required inputs
    if (selectedCalc.inputs) {
      for (const input of selectedCalc.inputs) {
        if (input.source.type === 'manual') {
          const value = manualInputs[input.id];
          if (value == null || isNaN(value)) {
            return false;
          }
        }
      }
      return true;
    }

    return false;
  };

  /**
   * Run the selected calculation
   */
  const handleRunCalculation = () => {
    if (!selectedCalc) return;

    try {
      setError(null);

      // Validate required inputs
      if (!areInputsValid()) {
        setError('Please fill in all required fields');
        return;
      }

      // Evaluate calculation using the engine
      const result = calculationEngine.evaluate(
        selectedCalc,
        experiment,
        manualInputs
      );

      // Check for NaN results
      if (isNaN(result.value)) {
        setError('Invalid calculation result. Please check your input values.');
        return;
      }

      // Add to results history
      setResults((prev) => [result, ...prev]);

      // Notify parent
      if (onResult) {
        onResult(result);
      }

      // Show success
      alert(
        `Calculation Complete!\n\n${selectedCalc.name}\n\nResult: ${formatCalcResult(result)}`
      );
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Unknown error';
      setError(errorMsg);
      console.error('Calculation error:', err);
    }
  };


  // Custom calculation form state
  const [newCalcName, setNewCalcName] = useState('');
  const [newCalcFormula, setNewCalcFormula] = useState('');
  const [newCalcUnit, setNewCalcUnit] = useState('');
  const [newCalcInputs, setNewCalcInputs] = useState<Array<{ id: string; label: string }>>([]);
  const [newInputLabel, setNewInputLabel] = useState('');

  const handleAddCustomInput = () => {
    if (!newInputLabel.trim()) return;
    const inputId = newInputLabel.toLowerCase().replace(/\s+/g, '_');
    setNewCalcInputs([...newCalcInputs, { id: inputId, label: newInputLabel }]);
    setNewInputLabel('');
  };

  const handleRemoveCustomInput = (index: number) => {
    setNewCalcInputs(newCalcInputs.filter((_, i) => i !== index));
  };

  const handleCreateCustomCalculation = () => {
    if (!newCalcName.trim() || !newCalcFormula.trim()) {
      alert('Please provide a name and formula');
      return;
    }

    // Validate formula
    const validation = validateFormula(newCalcFormula);
    if (!validation.valid) {
      alert(`Invalid formula: ${validation.error}`);
      return;
    }

    // Create custom calculation
    const customCalc: CalcDefinition = {
      id: `custom-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      name: newCalcName,
      scope: [experimentType],
      category: 'Variation', // Default category
      formula: newCalcFormula,
      outputUnit: newCalcUnit || undefined,
      inputs: newCalcInputs.map((input) => ({
        id: input.id,
        label: input.label,
        source: { type: 'manual' },
      })),
      description: `Custom calculation: ${newCalcName}`,
    };

    // Add to custom calculations
    const updated = [...customCalculations, customCalc];
    saveCustomCalculations(updated);

    // Reset form
    setNewCalcName('');
    setNewCalcFormula('');
    setNewCalcUnit('');
    setNewCalcInputs([]);
    setShowCustomCalcForm(false);

    // Select the new calculation
    setSelectedCalcId(customCalc.id);

    alert('Custom calculation created successfully!');
  };

  const handleDeleteCustomCalculation = (calcId: string) => {
    // if (!confirm('Are you sure you want to delete this custom calculation?')) return;
    // const updated = customCalculations.filter((c) => c.id !== calcId);
    // saveCustomCalculations(updated);
    // if (selectedCalcId === calcId) {
    //   setSelectedCalcId(null);
    // }
  };

  return (
    <div style={styles.container}>
      <h3 style={styles.heading}>Calculations</h3>

      {/* Experiment Type Badge */}
      <div style={styles.badge}>
        Current Type: <strong>{experimentType}</strong> | Reaction: <strong>{reactionType}</strong>
      </div>

      {/* Calculation Selector */}
      <div style={styles.section}>
        <div style={styles.selectorHeader}>
          <label style={styles.label}>Select Calculation</label>
          <button
            style={styles.addCustomButton}
            onClick={() => setShowCustomCalcForm(!showCustomCalcForm)}
          >
            <Plus size={14} />
            <span>Create Custom</span>
          </button>
        </div>
        <select
          style={styles.select}
          value={selectedCalcId || ''}
          onChange={(e) => handleSelectCalculation(e.target.value)}
        >
          <option value="">-- Choose a calculation --</option>
          {predefinedCalculations.length > 0 && (
            <optgroup label="Predefined Calculations">
              {predefinedCalculations.map((calc) => (
                <option key={calc.id} value={calc.id}>
                  {calc.name} ({calc.category})
                </option>
              ))}
            </optgroup>
          )}
          {customForCurrentScope.length > 0 && (
            <optgroup label="Custom Calculations">
              {customForCurrentScope.map((calc) => (
                <option key={calc.id} value={calc.id}>
                  {calc.name} (Custom)
                </option>
              ))}
            </optgroup>
          )}
        </select>
        {selectedCalc && (
          <div style={styles.calcInfo}>
            <p style={styles.description}>{selectedCalc.description}</p>
            {customCalculations.find((c) => c.id === selectedCalcId) && (
              <button
                style={styles.deleteCustomButton}
                onClick={() => handleDeleteCustomCalculation(selectedCalcId!)}
              >
                <X size={12} />
                Delete Custom Calculation
              </button>
            )}
          </div>
        )}
      </div>

      {/* Custom Calculation Form */}
      {showCustomCalcForm && (
        <div style={styles.customCalcForm}>
          <h4 style={styles.subheading}>Create Custom Calculation</h4>

          <div style={styles.formGroup}>
            <label style={styles.label}>
              Calculation Name <span style={styles.required}>*</span>
            </label>
            <input
              type="text"
              style={styles.input}
              value={newCalcName}
              onChange={(e) => setNewCalcName(e.target.value)}
              placeholder="e.g., My Custom Yield"
            />
          </div>

          <div style={styles.formGroup}>
            <label style={styles.label}>
              Formula <span style={styles.required}>*</span>
            </label>
            <input
              type="text"
              style={styles.input}
              value={newCalcFormula}
              onChange={(e) => setNewCalcFormula(e.target.value)}
              placeholder="e.g., (input1 + input2) * 100"
            />
            <p style={styles.formulaHint}>
              Use JavaScript syntax. Variables match input IDs. Example: (a + b) * 100
            </p>
          </div>

          <div style={styles.formGroup}>
            <label style={styles.label}>Output Unit (optional)</label>
            <input
              type="text"
              style={styles.input}
              value={newCalcUnit}
              onChange={(e) => setNewCalcUnit(e.target.value)}
              placeholder="e.g., %, g, mol"
            />
          </div>

          <div style={styles.formGroup}>
            <label style={styles.label}>Input Variables</label>
            <div style={styles.inputList}>
              {newCalcInputs.map((input, index) => (
                <div key={index} style={styles.inputItem}>
                  <span style={styles.inputItemLabel}>
                    {input.label} (ID: {input.id})
                  </span>
                  <button
                    style={styles.removeInputButton}
                    onClick={() => handleRemoveCustomInput(index)}
                  >
                    <X size={14} />
                  </button>
                </div>
              ))}
            </div>
            <div style={styles.addInputRow}>
              <input
                type="text"
                style={styles.input}
                value={newInputLabel}
                onChange={(e) => setNewInputLabel(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleAddCustomInput()}
                placeholder="Input label (e.g., Actual Yield)"
              />
              <button style={styles.addInputButton} onClick={handleAddCustomInput}>
                <Plus size={14} />
                Add Input
              </button>
            </div>
          </div>

          <div style={styles.formActions}>
            <button
              style={styles.createButton}
              onClick={handleCreateCustomCalculation}
              disabled={!newCalcName.trim() || !newCalcFormula.trim()}
            >
              Create Calculation
            </button>
            <button
              style={styles.cancelButton}
              onClick={() => {
                setShowCustomCalcForm(false);
                setNewCalcName('');
                setNewCalcFormula('');
                setNewCalcUnit('');
                setNewCalcInputs([]);
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Calculation Inputs */}
      {selectedCalc && (
        <div style={styles.section}>
          <h4 style={styles.subheading}>Input Values</h4>

          {/* Phase 4: Render variables */}
          {selectedCalc.variables && selectedCalc.variables.map((variable) => {
            const isManual = variable.source.type === 'manual';
            const isConstant = variable.source.type === 'constant';
            const isBomAggregate = variable.source.type === 'bomAggregate';
            const isAssay = variable.source.type === 'assay';

            let derivedValue: number | null = null;
            let derivedLabel = '';

            if (isBomAggregate) {
              derivedValue = getDerivedValue(variable.name, variable.source);
              const { role, field, mode } = variable.source;
              derivedLabel = `${mode} of ${field} from ${role} materials`;
            } else if (isAssay) {
              const found = experiment.assays?.find((a) => a.basis === variable.source.basis);
              derivedValue = found?.value ?? null;
              derivedLabel = `Assay (${variable.source.basis} basis)`;
            }

            return (
              <div key={variable.name} style={styles.formGroup}>
                <label style={styles.label}>
                  {variable.label}
                  {variable.unit && <span style={styles.unitLabel}> ({variable.unit})</span>}
                  {variable.required && <span style={styles.required}> *</span>}
                </label>

                {isConstant ? (
                  <div style={styles.derivedValue}>
                    <span style={styles.derivedLabel}>Constant:</span>
                    <span style={styles.derivedNumber}>{variable.source.value}</span>
                  </div>
                ) : isManual ? (
                  <input
                    type="number"
                    style={styles.input}
                    value={manualInputs[variable.name] ?? ''}
                    onChange={(e) => handleInputChange(variable.name, e.target.value)}
                    step="0.001"
                    placeholder={`Enter ${variable.label.toLowerCase()}`}
                  />
                ) : (
                  <div style={styles.derivedValue}>
                    <span style={styles.derivedLabel}>From {isBomAggregate ? 'BOM' : 'Assay'}:</span>
                    <span style={styles.derivedNumber}>
                      {derivedValue != null ? derivedValue.toFixed(3) : 'N/A'}
                    </span>
                    <span style={styles.derivedSource}>({derivedLabel})</span>
                  </div>
                )}
              </div>
            );
          })}

          {/* Phase 2: Legacy inputs (fallback) */}
          {!selectedCalc.variables && selectedCalc.inputs && selectedCalc.inputs.map((input) => {
            const isManual = input.source.type === 'manual';
            const derivedValue = !isManual ? getDerivedValue(input.id) : null;

            return (
              <div key={input.id} style={styles.formGroup}>
                <label style={styles.label}>
                  {input.label}
                  {isManual && <span style={styles.required}> *</span>}
                </label>

                {isManual ? (
                  <input
                    type="number"
                    style={styles.input}
                    value={manualInputs[input.id] ?? ''}
                    onChange={(e) => handleInputChange(input.id, e.target.value)}
                    step="0.001"
                    placeholder="Enter value"
                  />
                ) : (
                  <div style={styles.derivedValue}>
                    <span style={styles.derivedLabel}>From BOM:</span>
                    <span style={styles.derivedNumber}>{derivedValue?.toFixed(3) ?? 0}</span>
                    <span style={styles.derivedSource}>
                      (sum of {input.source.type === 'bomTotal' && input.source.role} materials)
                    </span>
                  </div>
                )}
              </div>
            );
          })}

          {/* Formula/Evaluator Display */}
          <div style={styles.formulaBox}>
            {selectedCalc.evaluator ? (
              <>
                <span style={styles.formulaLabel}>Evaluator:</span>
                <code style={styles.formulaCode}>{selectedCalc.evaluator}</code>
                {selectedCalc.description && (
                  <p style={styles.descriptionText}>{selectedCalc.description}</p>
                )}
              </>
            ) : selectedCalc.formula ? (
              <>
                <span style={styles.formulaLabel}>Formula:</span>
                <code style={styles.formulaCode}>{selectedCalc.formula}</code>
              </>
            ) : null}
          </div>

          {/* Error Display */}
          {error && (
            <div style={styles.errorBox}>
              <strong>Error:</strong> {error}
            </div>
          )}

          {/* Error Display */}
          {error && (
            <div style={styles.errorBox}>
              <strong>Error:</strong> {error}
            </div>
          )}

          {/* Run Button */}
          <button
            style={styles.calculateButton}
            onClick={handleRunCalculation}
            disabled={!areInputsValid()}
          >
            <Calculator size={16} style={{ flexShrink: 0 }} />
            <span>Run Calculation</span>
          </button>
        </div>
      )}

      {/* Results History */}
      {results.length > 0 && (
        <div style={styles.section}>
          <h4 style={styles.subheading}>
            <CheckCircle size={16} style={{ verticalAlign: 'middle', marginRight: '8px' }} />
            Results History
          </h4>
          <div style={styles.results}>
            {results.map((result, index) => {
              const calc = getCalculation(result.calcId) || customCalculations.find((c) => c.id === result.calcId);
              return (
                <div key={`${result.calcId}-${index}`} style={styles.resultCard}>
                  <div style={styles.resultHeader}>
                    <span style={styles.resultName}>{calc?.name || result.calcId}</span>
                    <span style={styles.resultTime}>
                      {result.timestamp?.toLocaleTimeString() || 'Now'}
                    </span>
                  </div>
                  <div style={styles.resultValue}>
                    {isNaN(result.value) ? (
                      <span style={{ color: '#dc2626' }}>Invalid result - check inputs</span>
                    ) : (
                      formatCalcResult(result)
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Help Section */}
      <div style={styles.helpBox}>
        <p style={styles.helpTitle}>Note</p>
        <p style={styles.helpText}>
          Calculations are data-driven from chemConfig.ts. Formulas are placeholder
          implementations. Values marked "From BOM" are automatically derived from
          your materials list.
        </p>
      </div>
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  container: {
    padding: '20px',
    height: '100%',
    overflowY: 'auto',
  },
  heading: {
    margin: '0 0 16px 0',
    fontSize: '18px',
    fontWeight: 600,
    color: '#1e293b',
  },
  badge: {
    display: 'inline-block',
    padding: '6px 12px',
    marginBottom: '20px',
    fontSize: '13px',
    color: '#1e40af',
    backgroundColor: '#dbeafe',
    borderRadius: '6px',
  },
  section: {
    marginBottom: '24px',
    paddingBottom: '24px',
    borderBottom: '1px solid #e2e8f0',
  },
  emptyState: {
    textAlign: 'center',
    padding: '60px 20px',
    backgroundColor: '#f8fafc',
    borderRadius: '8px',
  },
  emptyText: {
    margin: '0 0 8px 0',
    fontSize: '16px',
    fontWeight: 500,
    color: '#64748b',
  },
  emptySubtext: {
    margin: 0,
    fontSize: '14px',
    color: '#94a3b8',
  },
  label: {
    display: 'block',
    marginBottom: '6px',
    fontSize: '14px',
    fontWeight: 500,
    color: '#334155',
  },
  required: {
    color: '#ef4444',
  },
  select: {
    width: '100%',
    padding: '8px 12px',
    fontSize: '14px',
    border: '1px solid #cbd5e1',
    borderRadius: '6px',
    backgroundColor: '#fff',
    cursor: 'pointer',
    outline: 'none',
  },
  description: {
    margin: '8px 0 0 0',
    fontSize: '13px',
    color: '#64748b',
    fontStyle: 'italic',
  },
  subheading: {
    margin: '0 0 16px 0',
    fontSize: '15px',
    fontWeight: 600,
    color: '#334155',
  },
  formGroup: {
    marginBottom: '16px',
  },
  input: {
    width: '100%',
    padding: '8px 12px',
    fontSize: '14px',
    border: '1px solid #cbd5e1',
    borderRadius: '6px',
    outline: 'none',
    boxSizing: 'border-box',
  },
  derivedValue: {
    padding: '8px 12px',
    backgroundColor: '#f1f5f9',
    borderRadius: '6px',
    fontSize: '14px',
  },
  derivedLabel: {
    color: '#64748b',
    marginRight: '8px',
  },
  derivedNumber: {
    fontWeight: 600,
    color: '#0f172a',
    marginRight: '8px',
  },
  derivedSource: {
    fontSize: '12px',
    color: '#94a3b8',
    fontStyle: 'italic',
  },
  formulaBox: {
    padding: '12px',
    marginTop: '16px',
    backgroundColor: '#f8fafc',
    border: '1px solid #e2e8f0',
    borderRadius: '6px',
  },
  formulaLabel: {
    display: 'block',
    fontSize: '12px',
    fontWeight: 500,
    color: '#64748b',
    marginBottom: '4px',
  },
  formulaCode: {
    display: 'block',
    fontSize: '13px',
    fontFamily: 'monospace',
    color: '#0f172a',
    padding: '4px',
  },
  unitLabel: {
    fontSize: '12px',
    color: '#64748b',
    fontWeight: 'normal',
  },
  descriptionText: {
    margin: '8px 0 0 0',
    fontSize: '12px',
    color: '#64748b',
    fontStyle: 'italic',
  },
  errorBox: {
    padding: '12px',
    marginTop: '12px',
    backgroundColor: '#fee2e2',
    border: '1px solid #fecaca',
    borderRadius: '6px',
    color: '#991b1b',
    fontSize: '13px',
  },
  calculateButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    width: '100%',
    padding: '10px 20px',
    marginTop: '16px',
    fontSize: '14px',
    fontWeight: 500,
    color: '#fff',
    backgroundColor: '#2563eb',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    justifyContent: 'center',
  },
  results: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  resultCard: {
    padding: '12px',
    backgroundColor: '#f0fdf4',
    border: '1px solid #bbf7d0',
    borderRadius: '6px',
  },
  resultHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '8px',
  },
  resultName: {
    fontSize: '13px',
    fontWeight: 500,
    color: '#166534',
  },
  resultTime: {
    fontSize: '11px',
    color: '#16a34a',
  },
  resultValue: {
    fontSize: '24px',
    fontWeight: 700,
    color: '#15803d',
  },
  helpBox: {
    padding: '12px 16px',
    marginTop: '24px',
    backgroundColor: '#fffbeb',
    border: '1px solid #fde68a',
    borderRadius: '6px',
  },
  helpTitle: {
    margin: '0 0 4px 0',
    fontSize: '13px',
    fontWeight: 600,
    color: '#92400e',
  },
  helpText: {
    margin: 0,
    fontSize: '12px',
    color: '#78350f',
    lineHeight: '1.5',
  },
  selectorHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '8px',
  },
  addCustomButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    padding: '6px 12px',
    fontSize: '12px',
    fontWeight: 500,
    color: '#2563eb',
    backgroundColor: '#eff6ff',
    border: '1px solid #bfdbfe',
    borderRadius: '6px',
    cursor: 'pointer',
  },
  calcInfo: {
    marginTop: '8px',
  },
  deleteCustomButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    marginTop: '8px',
    padding: '4px 8px',
    fontSize: '11px',
    color: '#ef4444',
    backgroundColor: '#fee2e2',
    border: '1px solid #fecaca',
    borderRadius: '4px',
    cursor: 'pointer',
  },
  customCalcForm: {
    padding: '16px',
    marginBottom: '24px',
    backgroundColor: '#f8fafc',
    border: '2px solid #cbd5e1',
    borderRadius: '8px',
  },
  formulaHint: {
    margin: '4px 0 0 0',
    fontSize: '11px',
    color: '#64748b',
    fontStyle: 'italic',
  },
  inputList: {
    marginBottom: '8px',
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  inputItem: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '6px 10px',
    backgroundColor: '#fff',
    border: '1px solid #e2e8f0',
    borderRadius: '4px',
  },
  inputItemLabel: {
    fontSize: '13px',
    color: '#334155',
  },
  removeInputButton: {
    padding: '2px 4px',
    color: '#ef4444',
    backgroundColor: 'transparent',
    border: 'none',
    cursor: 'pointer',
  },
  addInputRow: {
    display: 'flex',
    gap: '8px',
  },
  addInputButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    padding: '8px 12px',
    fontSize: '12px',
    fontWeight: 500,
    color: '#fff',
    backgroundColor: '#10b981',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
  },
  formActions: {
    display: 'flex',
    gap: '8px',
    marginTop: '16px',
  },
  createButton: {
    flex: 1,
    padding: '10px 20px',
    fontSize: '14px',
    fontWeight: 500,
    color: '#fff',
    backgroundColor: '#2563eb',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
  },
  cancelButton: {
    padding: '10px 20px',
    fontSize: '14px',
    fontWeight: 500,
    color: '#64748b',
    backgroundColor: '#f1f5f9',
    border: '1px solid #cbd5e1',
    borderRadius: '6px',
    cursor: 'pointer',
  },
};
